import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ListChecks, Check, Plus } from "lucide-react";
import {
  fetchUserCollections,
  addUserCollectionItem,
  type ItemType,
} from "../api/user-collections";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { Skel } from "./Skeleton";

type RowStatus = "idle" | "pending" | "added" | "error";

function AddToListDialog({
  itemType,
  itemId,
  onClose,
}: {
  itemType: ItemType;
  itemId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<Record<string, RowStatus>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["user-collections"],
    queryFn: fetchUserCollections,
  });

  const mutation = useMutation({
    mutationFn: (listId: string) => addUserCollectionItem(listId, { itemType, itemId }),
    onMutate: (listId) => setStatus((s) => ({ ...s, [listId]: "pending" })),
    onSuccess: (_data, listId) => {
      setStatus((s) => ({ ...s, [listId]: "added" }));
      void qc.invalidateQueries({ queryKey: ["user-collections", listId] });
    },
    onError: (err, listId) => {
      // 409 means it's already in the list — treat that as "added" too.
      const already = err instanceof Error && /already/i.test(err.message);
      setStatus((s) => ({ ...s, [listId]: already ? "added" : "error" }));
    },
  });

  const lists = data?.collections ?? [];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth: 380,
          width: "90%",
          maxHeight: "70vh",
          overflow: "auto",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 16 }}>
          Add to list
        </h2>

        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skel height="40px" />
            <Skel height="40px" />
          </div>
        ) : lists.length === 0 ? (
          <EmptyState
            icon={<ListChecks size={18} />}
            title="No lists yet"
            hint="Create a custom list, then come back to add this."
            action={{
              label: "New list",
              onClick: () => {
                onClose();
                void navigate({ to: "/lists" });
              },
            }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lists.map((list) => {
              const rowStatus = status[list.id] ?? "idle";
              return (
                <div
                  key={list.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "10px 12px",
                    background: "var(--surface-sunken)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
                      {list.name}
                    </div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
                      {list.completedCount} / {list.itemCount}
                    </div>
                  </div>
                  <Button
                    variant={rowStatus === "added" ? "secondary" : "primary"}
                    size="sm"
                    disabled={rowStatus === "pending" || rowStatus === "added"}
                    leftIcon={rowStatus === "added" ? <Check size={14} /> : <Plus size={14} />}
                    onClick={() => mutation.mutate(list.id)}
                  >
                    {rowStatus === "added" ? "Added" : rowStatus === "pending" ? "Adding…" : "Add"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

export function AddToListButton({
  itemType,
  itemId,
  size = "md",
}: {
  itemType: ItemType;
  itemId: string;
  size?: "sm" | "md" | "lg";
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button variant="secondary" size={size} leftIcon={<ListChecks size={16} />} onClick={() => setOpen(true)}>
        Add to list
      </Button>
      {open && <AddToListDialog itemType={itemType} itemId={itemId} onClose={() => setOpen(false)} />}
    </>
  );
}
